import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  CompletedSessionHistoryItem,
  CompletedSessionHistoryResponse,
  SessionResult,
  Stint,
} from '@spectrace/shared';
import { LeaderboardService } from '../../services/leaderboard.service';
import { SessionService } from '../../services/session.service';
import { TeamContextService } from '../../services/team-context.service';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';

interface SessionListItem extends SessionResult {
  relation: 'hosted' | 'joined' | 'public';
  expanded?: boolean;
  stints?: Stint[];
}

@Component({
  selector: 'app-session-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './session-history.component.html',
  styleUrls: ['./session-history.component.css'],
})
export class SessionHistoryComponent implements OnInit {
  activeTab: 'active' | 'completed' = 'active';
  private allHostedSessions: SessionListItem[] = [];
  private allJoinedSessions: SessionListItem[] = [];
  private allPublicSessions: SessionListItem[] = [];
  hostedSessions: SessionListItem[] = [];
  joinedSessions: SessionListItem[] = [];
  publicSessions: SessionListItem[] = [];
  completedSessions: CompletedSessionHistoryItem[] = [];
  completedPagination: CompletedSessionHistoryResponse['pagination'] = {
    limit: 20,
    offset: 0,
    nextOffset: null,
    nextCursor: null,
    hasMore: false,
  };
  completedLoading = false;
  completedLoaded = false;

  loading = true;
  joiningSessionId: string | null = null;
  deletingSessionId: string | null = null;
  showEligibleOnly = false;
  error = '';
  infoMessage = '';

  constructor(
    private apiService: ApiService,
    private leaderboardService: LeaderboardService,
    private sessionService: SessionService,
    private teamContextService: TeamContextService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadSessions();
  }

  selectTab(tab: 'active' | 'completed'): void {
    const tabChanged = this.activeTab !== tab;
    this.activeTab = tab;
    if (tab === 'completed' && (tabChanged || !this.completedLoaded)) {
      this.loadCompletedSessions(false);
    }
  }

  refreshCurrentTab(): void {
    if (this.activeTab === 'completed') {
      this.loadCompletedSessions(false);
      return;
    }
    this.loadSessions();
  }

  loadSessions(): void {
    this.loading = true;
    const request$ = this.authService.isAuthenticated()
      ? this.apiService.getLeaderboardSessions()
      : this.apiService.getPublicSessions();

    request$.subscribe({
      next: (response) => {
        this.allHostedSessions = this.mapActiveTabSessions(
          response.hostedSessions,
          'hosted',
        );
        this.allJoinedSessions = this.mapActiveTabSessions(
          response.joinedSessions,
          'joined',
        );
        this.allPublicSessions = this.mapActiveTabSessions(
          response.publicSessions,
          'public',
        );
        this.applyEligibilityFilter();
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load sessions:', error);
        this.loading = false;
      },
    });
  }

  loadCompletedSessions(loadMore: boolean = false): void {
    this.completedLoading = true;
    const requestParams = {
      limit: this.completedPagination.limit,
      cursor: loadMore
        ? this.completedPagination.nextCursor || undefined
        : undefined,
      offset:
        loadMore && !this.completedPagination.nextCursor
          ? this.completedPagination.nextOffset || 0
          : 0,
    };

    this.sessionService.getCompletedSessionHistory(requestParams).subscribe({
      next: (response) => {
        this.completedSessions = loadMore
          ? [...this.completedSessions, ...response.sessions]
          : response.sessions;
        this.completedPagination = response.pagination;
        this.completedLoading = false;
        this.completedLoaded = true;
      },
      error: (error) => {
        console.error('Failed to load completed sessions:', error);
        this.completedLoading = false;
      },
    });
  }

  toggleSession(session: SessionListItem): void {
    session.expanded = !session.expanded;

    // Load stints if expanding and not already loaded
    if (session.expanded && !session.stints) {
      const request$ = this.authService.isAuthenticated()
        ? this.apiService.getLeaderboardSessionById(session.id)
        : this.apiService.getPublicSessionById(session.id);

      request$.subscribe({
        next: (response) => {
          session.stints = response.stints;
        },
        error: (error) => {
          console.error('Failed to load session stints:', error);
        },
      });
    }
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDuration(startTime: Date, endTime?: Date): string {
    const start = new Date(startTime).getTime();
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    const duration = Math.floor((end - start) / 1000 / 60); // minutes
    return `${duration} min`;
  }

  formatTime(milliseconds: number): string {
    if (milliseconds <= 0) {
      return '--:--.---';
    }

    const totalSeconds = milliseconds / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const ms = Math.floor(milliseconds % 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }

  getSessionSectorIndexes(session: SessionResult): number[] {
    const maxSectorCount = (session.allLaps || []).reduce(
      (max, lap) => Math.max(max, lap.sectors?.length || 0),
      0,
    );

    return Array.from({ length: maxSectorCount }, (_value, index) => index);
  }

  getLapSectorTime(lap: SessionResult['allLaps'][number], sectorIndex: number): string {
    const sectorTime = lap?.sectors?.[sectorIndex];
    return sectorTime ? this.formatTime(sectorTime) : '-';
  }

  getSessionStateLabel(
    session: SessionResult,
  ): 'Completed' | 'Scheduled' | 'Live' {
    if (session.status === 'completed') {
      return 'Completed';
    }

    const now = Date.now();
    const startTime = new Date(session.sessionDetails.startTime).getTime();
    const endTime = session.sessionDetails.endTime
      ? new Date(session.sessionDetails.endTime).getTime()
      : null;

    if (now < startTime) {
      return 'Scheduled';
    }

    if (endTime && now >= endTime) {
      return 'Completed';
    }

    return 'Live';
  }

  hasTeamOnlyBranding(session: SessionResult): boolean {
    return !session.isPublic && !!(session.teamName || session.teamLogoUrl);
  }

  getTeamLabel(session: SessionResult): string {
    return session.teamName || 'Team Session';
  }

  getTeamInitial(session: SessionResult): string {
    const label = this.getTeamLabel(session).trim();
    return label ? label.charAt(0).toUpperCase() : 'T';
  }

  getHostName(session: SessionResult): string {
    return session.hostName || session.playerName || 'Unknown host';
  }

  hasIRatingWindow(session: SessionResult): boolean {
    return (
      session.minIRating !== undefined || session.maxIRating !== undefined
    );
  }

  formatIRatingWindow(session: SessionResult): string {
    if (!this.hasIRatingWindow(session)) {
      return '';
    }

    if (
      session.minIRating !== undefined &&
      session.maxIRating !== undefined
    ) {
      return `${session.minIRating} - ${session.maxIRating}`;
    }

    if (session.minIRating !== undefined) {
      return `${session.minIRating}+`;
    }

    return `Up to ${session.maxIRating}`;
  }

  canShowEligibleToggle(): boolean {
    const currentUser = this.authService.getCurrentUser();
    return currentUser?.irating !== undefined && currentUser?.irating !== null;
  }

  toggleEligibleOnly(): void {
    if (!this.canShowEligibleToggle()) {
      this.showEligibleOnly = false;
      return;
    }

    this.showEligibleOnly = !this.showEligibleOnly;
    this.applyEligibilityFilter();
  }

  getLapsForStint(session: SessionResult, stintId: string): number {
    return session.allLaps.filter((lap) => lap.stintId === stintId).length;
  }

  openSession(session: SessionListItem, event: MouseEvent): void {
    event.stopPropagation();
    this.error = '';
    this.infoMessage = '';

    if (!this.authService.isAuthenticated()) {
      if (session.status === 'active') {
        this.router.navigate(['/login'], {
          queryParams: { returnUrl: `/session/${session.id}` },
        });
        return;
      }

      this.router.navigate(['/session', session.id]);
      return;
    }

    // Completed sessions are view-only and do not need join calls.
    if (session.status !== 'active') {
      this.alignTeamContextForSession(session);
      this.router.navigate(['/session', session.id]);
      return;
    }

    this.joiningSessionId = session.id;

    const openSessionRequest = this.sessionService.joinSession(session.id);

    openSessionRequest.subscribe({
      next: () => {
        this.alignTeamContextForSession(session);
        this.leaderboardService.refreshLeaderboard();
        this.loadSessions();
        this.joiningSessionId = null;
        this.router.navigate(['/session', session.id]);
      },
      error: (error) => {
        this.error = error.error?.error || 'Failed to open session';
        this.joiningSessionId = null;
      },
    });
  }

  getOpenButtonLabel(session: SessionListItem): string {
    if (this.joiningSessionId === session.id) {
      return 'Opening...';
    }
    if (!this.authService.isAuthenticated()) {
      return session.status === 'active' ? 'Login to Join' : 'View Session';
    }
    return session.status === 'active' ? 'Open Session' : 'View Session';
  }

  canDeleteSession(session: SessionListItem): boolean {
    return this.authService.isAuthenticated() && session.relation === 'hosted';
  }

  getCompletedRoleLabel(session: CompletedSessionHistoryItem): string {
    return session.userRole === 'HOST' ? 'Hosted by you' : 'Participated';
  }

  openCompletedSession(session: CompletedSessionHistoryItem): void {
    this.router.navigate(['/session', session.id]);
  }

  private alignTeamContextForSession(session: SessionListItem): void {
    const userTeams = this.teamContextService.getUserTeams();
    const matchingUserTeam = userTeams.find(
      (team) => team.id === session.teamId,
    );

    if (matchingUserTeam) {
      this.teamContextService.setCurrentTeam(matchingUserTeam);
      return;
    }

    // Public sessions from other teams should not be filtered out by the selected team context.
    if (session.relation === 'public') {
      this.teamContextService.setCurrentTeam(null);
    }
  }

  deleteSession(session: SessionListItem, event: MouseEvent): void {
    event.stopPropagation();
    this.error = '';
    this.infoMessage = '';

    if (!this.canDeleteSession(session)) {
      return;
    }

    const confirmed = confirm(
      `Delete session "${session.name}"? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    this.deletingSessionId = session.id;

    this.leaderboardService.deleteEntry(session.id).subscribe({
      next: () => {
        this.loadSessions();
        this.leaderboardService.refreshLeaderboard();
        this.deletingSessionId = null;
      },
      error: (error) => {
        this.error = error.error?.error || 'Failed to delete session';
        this.deletingSessionId = null;
      },
    });
  }

  viewRaceOverview(session: SessionListItem, event: MouseEvent): void {
    event.stopPropagation();
    this.error = '';
    this.infoMessage = '';
    this.router.navigate(['/race', session.id]);
  }

  viewCompletedRaceOverview(session: CompletedSessionHistoryItem, event: MouseEvent): void {
    event.stopPropagation();
    this.error = '';
    this.infoMessage = '';
    this.router.navigate(['/race', session.id]);
  }

  copySessionSnapshotLink(session: SessionListItem, event: MouseEvent): void {
    event.stopPropagation();
    this.error = '';
    this.infoMessage = '';

    if (session.isPublic) {
      const url = `${window.location.origin}/race/${session.id}`;
      navigator.clipboard.writeText(url).catch(() => undefined);
      this.infoMessage = 'Public snapshot link copied.';
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.error = 'Login required to share private session snapshots.';
      return;
    }

    this.apiService.createSessionShareLink(session.id, 'private', 168).subscribe({
      next: (response) => {
        navigator.clipboard.writeText(response.url).catch(() => undefined);
        this.infoMessage = 'Private snapshot link copied.';
      },
      error: (error) => {
        this.error = error?.error?.error || 'Failed to generate snapshot link.';
      },
    });
  }

  copyCompletedSnapshotLink(
    session: CompletedSessionHistoryItem,
    event: MouseEvent,
  ): void {
    event.stopPropagation();
    this.error = '';
    this.infoMessage = '';

    if (!this.authService.isAuthenticated()) {
      this.error = 'Login required to share completed session snapshots.';
      return;
    }

    this.apiService
      .createSessionShareLink(session.id, 'private', 168)
      .subscribe({
        next: (response) => {
          navigator.clipboard.writeText(response.url).catch(() => undefined);
          this.infoMessage = 'Snapshot link copied.';
        },
        error: (error) => {
          this.error =
            error?.error?.error || 'Failed to generate snapshot link.';
        },
      });
  }

  private mapActiveTabSessions(
    sessions: SessionResult[],
    relation: SessionListItem['relation'],
  ): SessionListItem[] {
    return sessions
      .filter((session) => this.getSessionStateLabel(session) !== 'Completed')
      .map((session) => ({
        ...session,
        relation,
        expanded: false,
      }));
  }

  private applyEligibilityFilter(): void {
    if (!this.canShowEligibleToggle()) {
      this.showEligibleOnly = false;
      this.hostedSessions = [...this.allHostedSessions];
      this.joinedSessions = [...this.allJoinedSessions];
      this.publicSessions = [...this.allPublicSessions];
      return;
    }

    if (!this.showEligibleOnly) {
      this.hostedSessions = [...this.allHostedSessions];
      this.joinedSessions = [...this.allJoinedSessions];
      this.publicSessions = [...this.allPublicSessions];
      return;
    }

    this.hostedSessions = this.allHostedSessions.filter((session) =>
      this.isEligibleForSession(session),
    );
    this.joinedSessions = this.allJoinedSessions.filter((session) =>
      this.isEligibleForSession(session),
    );
    this.publicSessions = this.allPublicSessions.filter((session) =>
      this.isEligibleForSession(session),
    );
  }

  private isEligibleForSession(session: SessionListItem): boolean {
    if (session.relation === 'hosted' || session.relation === 'joined') {
      return true;
    }

    if (!this.hasIRatingWindow(session)) {
      return true;
    }

    const currentUser = this.authService.getCurrentUser();
    const userIRating = currentUser?.irating;

    if (userIRating === undefined || userIRating === null) {
      return false;
    }

    if (
      session.minIRating !== undefined &&
      userIRating < session.minIRating
    ) {
      return false;
    }

    if (
      session.maxIRating !== undefined &&
      userIRating > session.maxIRating
    ) {
      return false;
    }

    return true;
  }
}
